import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import CustomMultiValueLabel from './CustomMultiValueLabel/CustomMultiValueLabel';
import ListFilterModal from '../../modals/ListFilterModal/ListFilterModal';
import { connectModalContainerContextToComponent } from '../../../../contexts/ModalContext/ModalContext';
import useFilters from '../../../hooks/useFilters/useFilters';
import AdvancedListSearchHeader from './AdvancedListSearchHeader/AdvancedListSearchHeader';
import AdvancedSearchUtility from '../../../../utility/AdvancedSearchUtility';
import { compareArrays } from '../../../../utility/editArrayOfObjectsUtil';
import {
  clearAppliedAlertSearch,
  selectActiveSearchParams,
  selectIsAlertListLocalStorageLoaded,
  setAppliedAlertSearch,
} from '../../../../redux/slices/alertListSlice/alertListSlice';
import {
  clearAppliedDeviceSearch,
  selectDeviceListActiveSearchParams,
  setAppliedDeviceSearch,
} from '../../../../redux/slices/filteredDeviceListSlice/filteredDeviceListSlice';
import SearchType from '../../../../utility/types/SearchType';
import {
  selectSavedSearchById,
  selectSavedSearches,
} from '../../../../redux/slices/savedSearchesSlice/savedSearchesSlice';

function AdvancedListSearch({
  searchType,
  getSearchedResults,
  setModalJSX,
  setListCurrPage,
}) {
  const dispatch = useDispatch();
  const savedSearches = useSelector(selectSavedSearches);
  const alertActiveSearchParams = useSelector(selectActiveSearchParams);
  const deviceActiveSearchParams = useSelector(selectDeviceListActiveSearchParams);
  const setAppliedSearch = searchType === SearchType.ALERT
    ? setAppliedAlertSearch : setAppliedDeviceSearch;
  const activeSearchParams = searchType === SearchType.ALERT
    ? alertActiveSearchParams
    : deviceActiveSearchParams;
  const activeSavedSearch = useSelector(
    (state) => selectSavedSearchById(state, activeSearchParams.id),
  );
  const [activeSavedSearchToEdit, setActiveSavedSearchToEdit] = useState(
    activeSavedSearch || null,
  );
  const [selectedSearchFilterOptions, setSelectedSearchFilterOptions] = useState([]);
  const [isEditingSavedSearch, setIsEditingSavedSearch] = useState(false);

  const isAlertListLocalStorageLoaded = useSelector(selectIsAlertListLocalStorageLoaded);
  const selectRef = React.createRef();

  const getInitialFilters = () => activeSearchParams.filters;

  const {
    filters,
    setFilters,
    updateFilters,
    removeFilter,
    getFilterTooltip,
    getFilterByFacetTypeId,
  } = useFilters({
    initialFilters: getInitialFilters(),
    searchType,
  });

  const options = AdvancedSearchUtility.getFilterSelectOptions(searchType);

  /* istanbul ignore next */
  const updateMultiSelectValues = (newValues) => {
    const savedSearchFilterOptions = AdvancedSearchUtility.getSelectedOptionsByValue(
      options,
      newValues,
    );
    setSelectedSearchFilterOptions(savedSearchFilterOptions);
  };

  /* istanbul ignore next */
  const handleUpdateFilters = () => {
    if (activeSavedSearchToEdit) {
      const updatedSavedSearch = {
        ...activeSavedSearchToEdit,
        filters,
      };
      setActiveSavedSearchToEdit(updatedSavedSearch);
      updateMultiSelectValues(updatedSavedSearch.filters);
    } else {
      updateMultiSelectValues(filters);
    }
    // submit POST request with updated filters
    setListCurrPage(0);
    getSearchedResults(filters);
    if (searchType === SearchType.ALERT) {
      dispatch(setAppliedAlertSearch({ filters }));
    } else {
      dispatch(setAppliedDeviceSearch({ filters }));
    }
  };

  const handleOpenNewFilterModal = (facetId, selectValues) => {
    const filterToEdit = getFilterByFacetTypeId(facetId);
    // close select dropdown menu
    if (selectRef && selectRef.current) {
      selectRef.current.blur();
    }
    setModalJSX(
      <ListFilterModal
        facetTypeId={facetId}
        searchType={searchType}
        facetSelectOptions={[]}
        filterToEdit={filterToEdit}
        updateFilters={updateFilters}
        getSearchedResults={getSearchedResults}
        handleSetSelectedSearchFilters={
              /* istanbul ignore next */
              () => setSelectedSearchFilterOptions(selectValues)
            }
      />,
    );
  };

  const handleMultiSelectOnChange = (values, action) => {
    if (action.action === 'select-option') {
      handleOpenNewFilterModal(
        values.length > 0
          ? values[values.length - 1].value
          : [],
        values,
      );
    }
    /* istanbul ignore next */
    if (action.action === 'remove-value') {
      removeFilter(action.removedValue.value);
    }
  };

  /* istanbul ignore next */
  const handleMultiValueClick = (e, value) => {
    e.stopPropagation();
    e.preventDefault();
    const activeOption = options.find((option) => option.value === value);
    handleOpenNewFilterModal(activeOption.value, selectedSearchFilterOptions);
  };

  const handleClearSearch = () => {
    const discardAppliedSearch = searchType === SearchType.ALERT
      ? clearAppliedAlertSearch : clearAppliedDeviceSearch;
    setSelectedSearchFilterOptions([]);
    setFilters([]);
    setActiveSavedSearchToEdit(null);
    dispatch(discardAppliedSearch());
    setIsEditingSavedSearch(false);
  };

  React.useEffect(() => {
    setActiveSavedSearchToEdit(activeSavedSearch);
  }, [activeSavedSearch]);

  React.useEffect(() => {
    if (isAlertListLocalStorageLoaded) {
      handleUpdateFilters();
    }
  }, [filters, isAlertListLocalStorageLoaded]);

  React.useEffect(() => {
    if (activeSavedSearch && activeSavedSearchToEdit && !isEditingSavedSearch) {
      if (activeSavedSearchToEdit.filters.length > 0) {
        if (compareArrays(
          activeSavedSearchToEdit.filters,
          filters,
        ) === false) {
          setIsEditingSavedSearch(
            true,
          );
        }
      }
    }
  }, [activeSavedSearchToEdit, filters]);
  /* istanbul ignore next */
  return (
    <>
      <AdvancedListSearchHeader
        savedSearches={savedSearches}
        activeSavedSearch={activeSavedSearchToEdit}
        setActiveSavedSearchToEdit={(search) => {
          dispatch(setAppliedSearch(search));
          setActiveSavedSearchToEdit(search);
        }}
        filters={filters}
        setFilters={setFilters}
        isEditingSavedSearch={isEditingSavedSearch}
        setIsEditingSavedSearch={setIsEditingSavedSearch}
        handleClearSearch={handleClearSearch}
        handleDiscardSavedSearchChanges={() => {
          dispatch(setAppliedSearch(activeSavedSearch));
          setActiveSavedSearchToEdit(activeSavedSearch);
          setFilters(activeSavedSearch.filters);
          setIsEditingSavedSearch(false);
        }}
        entityTypeId={searchType}
      />
      <div>
        <form data-testid="form">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="sr-only" htmlFor="filterType">Filter Type</label>
          <Select
            name="filterType"
            inputId="filterType"
            isMulti
            styles={{
              container: (base) => ({
                ...base,
                width: '100%',
              }),
            }}
            isClearable={false}
            components={{
              MultiValueLabel: (props) => (
                /* istanbul ignore next */
                <CustomMultiValueLabel
                  handleMultiValueClick={handleMultiValueClick}
                  getFilterTooltip={getFilterTooltip}
                  searchType={searchType}
                  {...props}
                />
              ),
            }}
            options={options}
            onChange={handleMultiSelectOnChange}
            value={selectedSearchFilterOptions}
            placeholder="Add a filter"
            aria-label="add a filter"
            ref={selectRef}
          />
        </form>
      </div>
    </>
  );
}

AdvancedListSearch.propTypes = {
  searchType: PropTypes.number.isRequired,
  getSearchedResults: PropTypes.func.isRequired,
  setModalJSX: PropTypes.func.isRequired,
  setListCurrPage: PropTypes.func.isRequired,
};
export { AdvancedListSearch };
export default connectModalContainerContextToComponent(AdvancedListSearch);
